import React from "react";

import { Dash, Svg, TooltipIcon, legacyTooltipAttrs } from "../svg";

/**
 * Native port of ``cms/blocks/hc-block/hc-block--compare-breeze-2.html`` (#33404).
 *
 * Class-driven structural compare table; imagery is CSS background-image keyed
 * off element classes. The ``__react_component_tooltip`` static markup is
 * reproduced VERBATIM (a global JS tooltip lib rebinds to it). Tooltips here
 * carry the ``wrap mobile-margin`` outer class and all reuse ``id="tooltip-0"``
 * (intentional legacy markup). The legacy ``{% include 'cms/components/svg.html'
 * %}`` includes become the shared ``Svg``/``TooltipIcon``/``Dash`` inline
 * components.
 */

// This table's tooltip trigger lives inside an outer ``span.wrap mobile-margin``
// that also carries ``data-tooltip``/``title``; the trailing copy text is passed
// as ``children`` (it precedes the trigger span in the source).
const Tooltip = (props: { text: string }): React.JSX.Element => (
    <>
        <span data-tip="true" data-for="tooltip-0" {...legacyTooltipAttrs}>
            <Svg
                Icon={TooltipIcon}
                src="tooltip-icon"
                className="tooltip__handle"
            />
        </span>
        <div
            className="__react_component_tooltip place-top type-dark tooltip__content"
            id="tooltip-0"
            role="tooltip"
            data-id="tooltip"
            style={{ maxWidth: "275px" }}
        >
            {props.text}
        </div>
    </>
);

export const CompareBreeze2 = (): React.JSX.Element => (
    <div className="l-capped-width">
        <header>
            <h2>
                Compare TEMPUR-Breeze<sup>®</sup>
            </h2>
            <p>
                Take a closer look at Tempur-Pedic’s best-rated mattress
                collection
            </p>
        </header>
        <div>
            <table>
                <thead>
                    <tr>
                        <th></th>
                        <th>Cooling</th>
                        <th>
                            Cooling
                            <br />
                            Technology
                        </th>
                        <th>
                            <span>
                                Power Base
                                <br />
                                Included
                            </span>
                        </th>
                        <th>Profile</th>
                        <th>Available Feels</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <span>
                                ProBreeze<sup>®</sup>
                            </span>
                        </td>
                        <td>
                            <div className="cooling-number-container">
                                <span>Up to</span>
                                <strong className="cooling-number">5°</strong>
                                <span>
                                    cooler<sup>++</sup>
                                </span>
                            </div>
                        </td>
                        <td>
                            <span>
                                <span>
                                    Pure Cool<sup>®</sup>
                                </span>
                                <br />
                                <span className="switch-wrapping">
                                    Material{" "}
                                    <span
                                        className="wrap mobile-margin"
                                        data-tooltip="Designed to pull heat away from your body to help you feel cooler as you sleep."
                                        title="Designed to pull heat away from your body to help you feel cooler as you sleep."
                                    >
                                        <Tooltip text="Designed to pull heat away from your body to help you feel cooler as you sleep." />
                                    </span>
                                </span>
                            </span>
                        </td>
                        <td>
                            <Svg Icon={Dash} src="dash" className="dash" />
                        </td>
                        <td className="profile-height">
                            <strong>
                                12<sup>&quot;</sup>
                            </strong>
                        </td>
                        <td>
                            <span>Medium</span>
                            <div className="switch-wrapping">
                                Medium Hybrid{" "}
                                <span
                                    className="wrap mobile-margin"
                                    data-tooltip="Over 1000 premium innersprings help deliver the perfect combination of pressure relief and support."
                                    title="Over 1000 premium innersprings help deliver the perfect combination of pressure relief and support."
                                >
                                    <Tooltip text="Over 1000 premium innersprings help deliver the perfect combination of pressure relief and support." />
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <span>
                                LuxeBreeze<sup>®</sup>
                            </span>
                        </td>
                        <td>
                            <div className="cooling-number-container">
                                <span>Up to</span>
                                <strong className="cooling-number">10°</strong>
                                <span>
                                    cooler<sup>++</sup>
                                </span>
                            </div>
                        </td>
                        <td>
                            <span>
                                <span>
                                    Pure Cool<sup>®</sup> Plus
                                </span>
                                <br />
                                <span className="switch-wrapping">
                                    Material{" "}
                                    <span
                                        className="wrap mobile-margin"
                                        data-tooltip="Our most advanced cooling technology — so even the hottest of sleepers can stay comfortable all night long."
                                        title="Our most advanced cooling technology — so even the hottest of sleepers can stay comfortable all night long."
                                    >
                                        <Tooltip text="Our most advanced cooling technology — so even the hottest of sleepers can stay comfortable all night long." />
                                    </span>
                                </span>
                            </span>
                        </td>
                        <td>
                            <Svg Icon={Dash} src="dash" className="dash" />
                        </td>
                        <td className="profile-height">
                            <strong>
                                13<sup>&quot;</sup>
                            </strong>
                        </td>
                        <td>
                            Soft
                            <div className="switch-wrapping">
                                Medium Hybrid{" "}
                                <span
                                    className="wrap mobile-margin"
                                    data-tooltip="TEMPUR-Material™ plus more than 1,300 premium, double-stacked innersprings provide a lighter, more responsive feel and easier movement in bed."
                                    title="TEMPUR-Material™ plus more than 1,300 premium, double-stacked innersprings provide a lighter, more responsive feel and easier movement in bed."
                                >
                                    <Tooltip text="TEMPUR-Material™ plus more than 1,300 premium, double-stacked innersprings provide a lighter, more responsive feel and easier movement in bed." />
                                </span>
                            </div>
                            <div>Firm</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <span>
                                ActiveBreeze<sup>®</sup>
                            </span>
                        </td>
                        <td>
                            <div className="cooling-number-container">
                                <span>Range of</span>
                                <strong className="cooling-number">30°</strong>
                                <span>
                                    cooler or warmer<sup>+++</sup>
                                </span>
                            </div>
                        </td>
                        <td>
                            <span>
                                <span>ActiveAir</span>{" "}
                                <span className="switch-wrapping">
                                    <span>
                                        Technology<sup>®</sup>
                                    </span>{" "}
                                    <span
                                        className="wrap mobile-margin"
                                        data-tooltip="The combination of the TEMPUR-Ergo® ProSmart® Air Base, TEMPUR-ActiveBreeze® hybrid mattress, and easy-to-use controls to deliver personalized climate control"
                                        title="The combination of the TEMPUR-Ergo® ProSmart® Air Base, TEMPUR-ActiveBreeze® hybrid mattress, and easy-to-use controls to deliver personalized climate control"
                                    >
                                        <Tooltip text="The combination of the TEMPUR-Ergo® ProSmart® Air Base, TEMPUR-ActiveBreeze® hybrid mattress, and easy-to-use controls to deliver personalized climate control" />
                                    </span>
                                </span>
                            </span>
                        </td>
                        <td>
                            <span className="switch-wrapping">
                                <span>
                                    Tempur-Ergo<sup>®</sup> ProSmart
                                    <sup>®</sup>
                                    <br />
                                    <span>Air Base</span>
                                </span>{" "}
                                <span
                                    className="wrap mobile-margin"
                                    data-tooltip="Features specially designed fans to quietly circulate cool or warm air directly from the base and up through the mattress"
                                    title="Features specially designed fans to quietly circulate cool or warm air directly from the base and up through the mattress"
                                >
                                    <Tooltip text="Features specially designed fans to quietly circulate cool or warm air directly from the base and up through the mattress" />
                                </span>
                            </span>
                        </td>
                        <td className="profile-height">
                            <strong>
                                13<sup>&quot;</sup>
                            </strong>
                        </td>
                        <td>
                            <div className="switch-wrapping">
                                Medium Hybrid{" "}
                                <span
                                    className="wrap mobile-margin"
                                    data-tooltip="Ventilated TEMPUR-Material™ plus a layer of premium innersprings provide consistent airflow from the TEMPUR-Ergo® ProSmart® Air Base for all night cooling."
                                    title="Ventilated TEMPUR-Material™ plus a layer of premium innersprings provide consistent airflow from the TEMPUR-Ergo® ProSmart® Air Base for all night cooling."
                                >
                                    <Tooltip text="Ventilated TEMPUR-Material™ plus a layer of premium innersprings provide consistent airflow from the TEMPUR-Ergo® ProSmart® Air Base for all night cooling." />
                                </span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
);
